// Worn detail for creature prefabs: faces, helmets, belts, boots, gloves, claws, pauldrons.
//
// The authored humanoids are a few flat blocks with two dark dots for eyes; at play zoom a unit
// is a coloured lozenge. This pass repaints voxels that already exist — it adds only bridge connectors between existing voxels, and never removes or
// moves one, so every unit keeps its footprint and height — to give each a readable face and
// something to wear, and closes gaps between pieces (`bridge.ts`: a neck under a floating head).
// It is data, run once when a prefab is registered (`withDetail`), so lo-fi,
// hi-fi and the smooth surface all see the same unit.
//
// Team voxels and glowing voxels are left alone: they carry meaning (owner colour, magic).
import { bridgeGaps } from './bridge.js';
import { repairHoles } from './repairs.js';
import { withTeamParts } from './teamParts.js';
import type { BlockVoxel, CharacterPart, CharacterPrefab } from './types.js';

const PLANNED_HEADLESS = new Set(['wagon']);

type Family = 'armoured' | 'leather' | 'robed' | 'undead' | 'beast';

const FAMILY: Readonly<Record<string, Family>> = {
  knight: 'armoured',
  paladin: 'armoured',
  templar: 'armoured',
  dark_templar: 'armoured',
  death_knight: 'armoured',
  guard: 'armoured',
  warlord: 'armoured',
  soldier: 'armoured',
  zealot: 'armoured',
  luminarch: 'armoured',
  bowman: 'leather',
  bandit: 'leather',
  spy: 'leather',
  explorer: 'leather',
  settler: 'leather',
  engineer: 'leather',
  bronze_smith: 'leather',
  orc: 'leather',
  goblin: 'leather',
  ogre: 'leather',
  orc_wolf_rider: 'leather',
  archmage: 'robed',
  mage: 'robed',
  wizard: 'robed',
  sorcerer: 'robed',
  cleric: 'robed',
  seraphim: 'robed',
  blood_priest: 'robed',
  chaos_lord: 'robed',
  lich: 'robed',
  wraith: 'robed',
  banshee: 'robed',
  skeleton: 'undead',
  skeleton_archer: 'undead',
  zombie: 'undead',
  demon: 'beast',
  dragon: 'beast',
};

interface Eye {
  readonly color: string;
  readonly glow?: string;
}
const EYES: Readonly<Record<string, Eye>> = {
  skeleton: { color: '#9dffc4', glow: '#3cff9a' },
  skeleton_archer: { color: '#9dffc4', glow: '#3cff9a' },
  zombie: { color: '#d8e06a', glow: '#8a9a20' },
  lich: { color: '#9dffe0', glow: '#2affc0' },
  wraith: { color: '#a8c8ff', glow: '#5a8aff' },
  banshee: { color: '#bfe8ff', glow: '#6ac0ff' },
  death_knight: { color: '#ff6a3a', glow: '#ff3a10' },
  dark_templar: { color: '#c060ff', glow: '#9020ff' },
  demon: { color: '#ff5a2a', glow: '#ff2a00' },
  dragon: { color: '#ffd23a', glow: '#ffa000' },
  blood_priest: { color: '#ff4a4a', glow: '#d01010' },
  chaos_lord: { color: '#ff8a2a', glow: '#e05a00' },
  orc: { color: '#ffd84a' },
  ogre: { color: '#ffe27a' },
  goblin: { color: '#fff07a' },
  orc_wolf_rider: { color: '#ffd84a' },
};
const LIVING_EYE: Eye = { color: '#f4f0e6' };

const LEATHER = '#4a3020';
const LEATHER_DARK = '#2d1d12';
const STEEL = '#9aa3ad';
const GOLD = '#d4a937';
const BONE = '#e2d9bd';
const MOUTH = '#6a2a2a';
const BROW = '#2a1c14';
const TUSK = '#f2ead0';

const BRUTES = new Set(['orc', 'ogre', 'goblin', 'orc_wolf_rider']);

const key = (v: { x: number; y: number; z: number }) => `${v.x},${v.y},${v.z}`;

/** A part's voxels, edited by position: `set` repaints a voxel that exists, and nothing else. */
class Painter {
  readonly byPos = new Map<string, BlockVoxel>();
  constructor(readonly voxels: readonly BlockVoxel[]) {
    for (const v of voxels) this.byPos.set(key(v), v);
  }
  get minX() {
    return Math.min(...this.voxels.map((v) => v.x));
  }
  get maxX() {
    return Math.max(...this.voxels.map((v) => v.x));
  }
  get minY() {
    return Math.min(...this.voxels.map((v) => v.y));
  }
  get maxY() {
    return Math.max(...this.voxels.map((v) => v.y));
  }
  get maxZ() {
    return Math.max(...this.voxels.map((v) => v.z));
  }
  at(x: number, y: number, z: number): BlockVoxel | undefined {
    return this.byPos.get(`${x},${y},${z}`);
  }
  set(x: number, y: number, z: number, patch: Partial<BlockVoxel>): void {
    const v = this.at(x, y, z);
    if (!v || v.team || v.emissive) return;
    this.byPos.set(key(v), { ...v, ...patch });
  }
  /** Every voxel in a horizontal layer. */
  layer(y: number): BlockVoxel[] {
    return [...this.byPos.values()].filter((v) => v.y === y);
  }
  out(): BlockVoxel[] {
    return this.voxels.map((v) => this.byPos.get(key(v)) ?? v);
  }
}

const METAL = { metalness: 0.85, roughness: 0.35 };
const CLOTH = { roughness: 0.9 };

/** The front-most voxel of each (x, y) column of a head: its visible face. */
function frontCells(head: Painter): Map<string, BlockVoxel> {
  const cells = new Map<string, BlockVoxel>();
  for (const v of head.voxels) {
    const k = `${v.x},${v.y}`;
    const held = cells.get(k);
    if (!held || v.z > held.z) cells.set(k, v);
  }
  return cells;
}

function paintFace(head: Painter, id: string, family: Family | undefined): void {
  const eye = EYES[id] ?? LIVING_EYE;
  const cells = frontCells(head);
  const front = head.maxZ;
  // Only the face counts: cells at (or one behind) the front-most layer.
  const face = [...cells.values()].filter((v) => v.z >= front - 1);
  if (face.length < 4) return;
  const ys = face.map((v) => v.y);
  const y0 = Math.min(...ys);
  const y1 = Math.max(...ys);
  const eyeY = y0 + Math.max(1, Math.round((y1 - y0) * 0.62));
  const cx = (Math.min(...face.map((v) => v.x)) + Math.max(...face.map((v) => v.x))) / 2;
  const at = (x: number, y: number) => cells.get(`${x},${y}`);
  const eyePatch: Partial<BlockVoxel> = eye.glow
    ? { color: eye.color, emissive: eye.glow, roughness: 0.3 }
    : { color: eye.color, roughness: 0.3 };
  const row = face
    .filter((v) => v.y === eyeY)
    .map((v) => v.x)
    .sort((a, b) => a - b);
  if (row.length < 2) return;
  // The two cells of the eye row that sit either side of the middle, one apart from it.
  const left = row.filter((x) => x < cx).pop();
  const right = row.find((x) => x > cx);
  const eyeXs = [left, right].filter((x): x is number => x !== undefined);
  const paintCell = (x: number, y: number, patch: Partial<BlockVoxel>, force = false) => {
    const v = at(x, y);
    if (!v || (!force && (v.team || v.emissive))) return;
    head.byPos.set(key(v), { ...v, ...patch });
  };
  for (const x of eyeXs) {
    paintCell(x, eyeY, eyePatch, true);
    // A brow above frames the eye so it reads at a distance; a dark socket below keeps a pale eye off pale skin.
    if (family !== 'undead') paintCell(x, eyeY + 1, { color: BROW, ...CLOTH });
    paintCell(x, eyeY - 1, { color: eye.glow ? '#10181a' : '#7a5a4a', ...CLOTH });
  }
  // Mouth: a dark line low in the face, tusks for the brutes and fangs for the demons.
  const mouthY = Math.max(y0, eyeY - 2);
  const mx = Math.round(cx - 0.5);
  for (const x of [mx, mx + 1]) paintCell(x, mouthY, { color: MOUTH, ...CLOTH });
  if (['orc', 'ogre', 'goblin', 'demon', 'orc_wolf_rider'].includes(id)) {
    paintCell(mx - 1, mouthY, { color: TUSK, roughness: 0.5 });
    paintCell(mx + 2, mouthY, { color: TUSK, roughness: 0.5 });
  }
  // A nose: a shade off the skin, centred between the eyes and the mouth.
  const nose = at(mx, eyeY - 1);
  if (nose && family !== 'undead') paintCell(mx, eyeY - 1, { color: shade(nose.color, 0.82) });
}

function paintHelmet(head: Painter, id: string, family: Family | undefined): void {
  if (family !== 'armoured' || head.maxY - head.minY < 4) return;
  const top = head.maxY;
  const eyeRow = head.minY + Math.max(1, Math.round((top - head.minY + 1) * 0.55));
  const faceZ = head.maxZ - 1;
  for (const y of [top, top - 1]) {
    for (const v of head.layer(y)) head.set(v.x, v.y, v.z, { color: STEEL, ...METAL });
  }
  // A brim and cheek plates in steel, leaving the face row open.
  for (const v of [...head.byPos.values()]) {
    if (v.y === top - 2 && v.z < faceZ) head.set(v.x, v.y, v.z, { color: STEEL, ...METAL });
    if (v.y < eyeRow && v.z < faceZ && (v.x === head.minX || v.x === head.maxX)) {
      head.set(v.x, v.y, v.z, { color: STEEL, ...METAL });
    }
  }
  if (id === 'death_knight' || id === 'dark_templar') {
    // A cold plume down the back of the helm.
    for (const v of head.layer(top)) head.set(v.x, v.y, v.z, { color: '#3a2a4a', ...METAL });
  }
}

function paintBody(body: Painter, family: Family | undefined, id: string): void {
  const h = body.maxY - body.minY + 1;
  const beltY = body.minY + Math.max(0, Math.round(h * 0.28));
  const belt = family === 'robed' ? '#c0a050' : family === 'undead' ? '#5a4a38' : LEATHER;
  for (const v of body.layer(beltY)) body.set(v.x, v.y, v.z, { color: belt, ...CLOTH });
  // Buckle on the front, a pouch at the hip.
  const front = body.layer(beltY).reduce((m, v) => Math.max(m, v.z), Number.NEGATIVE_INFINITY);
  const mid = Math.round((body.minX + body.maxX) / 2);
  body.set(mid, beltY, front, { color: GOLD, metalness: 0.9, roughness: 0.3 });
  body.set(body.maxX, beltY, front, { color: LEATHER_DARK, ...CLOTH });
  // A collar, and (but for robes) a baldric across the chest.
  for (const v of body.layer(body.maxY)) {
    body.set(v.x, v.y, v.z, { color: shade(v.color, family === 'armoured' ? 1.25 : 0.7) });
  }
  // The brutes wear no baldric: it reads as a dotted scar on a body that wide.
  if (family !== 'robed' && family !== 'undead' && !BRUTES.has(id)) {
    let step = 0;
    for (let y = body.maxY - 1; y > beltY; y--) {
      const x = body.minX + step++;
      body.set(x, y, front, { color: LEATHER, ...CLOTH });
    }
  }
}

function paintLimb(limb: Painter, kind: 'arm' | 'leg', family: Family | undefined): void {
  const low = limb.minY;
  if (kind === 'leg') {
    if (family === 'undead') return;
    const boot = family === 'armoured' ? STEEL : LEATHER_DARK;
    const fx = family === 'armoured' ? METAL : CLOTH;
    for (const y of [low, low + 1])
      for (const v of limb.layer(y)) limb.set(v.x, v.y, v.z, { color: boot, ...fx });
    for (const v of limb.layer(low + 2))
      limb.set(v.x, v.y, v.z, { color: shade(boot, 1.5), ...CLOTH });
    return;
  }
  // Arms: a glove or a gauntlet, claws for the beasts and the dead; a pauldron on top for armour.
  const claw = family === 'beast' || family === 'undead';
  for (const v of limb.layer(low)) {
    limb.set(
      v.x,
      v.y,
      v.z,
      claw
        ? { color: BONE, roughness: 0.5 }
        : family === 'armoured'
          ? { color: STEEL, ...METAL }
          : { color: LEATHER_DARK, ...CLOTH },
    );
  }
  if (!claw)
    for (const v of limb.layer(low + 1))
      limb.set(v.x, v.y, v.z, { color: shade(LEATHER, 1.2), ...CLOTH });
  if (family === 'armoured') {
    for (const v of limb.layer(limb.maxY)) limb.set(v.x, v.y, v.z, { color: STEEL, ...METAL });
  }
}

/** Scale a #rrggbb colour's brightness. */
function shade(hex: string, f: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const c = (s: number) => Math.max(0, Math.min(255, Math.round(((n >> s) & 255) * f)));
  return `#${[16, 8, 0].map((s) => c(s).toString(16).padStart(2, '0')).join('')}`;
}

function paint(
  id: string,
  part: CharacterPart,
  family: Family | undefined,
  helmed: boolean,
): CharacterPart {
  if (part.voxels.length < 4) return part;
  const p = new Painter(part.voxels);
  if (/head/i.test(part.name)) {
    paintFace(p, id, family);
    if (!helmed) paintHelmet(p, id, family);
  } else if (/^(body|torso|chest)$/i.test(part.name)) {
    paintBody(p, family, id);
  } else if (/arm/i.test(part.name)) {
    paintLimb(p, 'arm', family);
  } else if (/leg/i.test(part.name)) {
    paintLimb(p, 'leg', family);
  } else {
    return part;
  }
  return { ...part, voxels: p.out() };
}

/** The prefab with its gaps closed, a face and worn detail. Ships and props pass through. */
export function withDetail(id: string, prefab: CharacterPrefab): CharacterPrefab {
  if (/^legend_/.test(id)) return prefab; // champions are authored by hand (and as models)
  const hasHead = prefab.parts.some((p) => /head/i.test(p.name));
  // A unit drawn with a hood and no head part (the blood priest) still has holes to close.
  const hasHood = prefab.parts.some((p) => /hood/i.test(p.name));
  if (!hasHead && !hasHood) {
    // Headless props (a wagon) have no face to paint but may still wear the owner's colour.
    return PLANNED_HEADLESS.has(id)
      ? { ...prefab, parts: withTeamParts(id, 'leather', prefab.parts) }
      : prefab;
  }
  const family =
    FAMILY[id] ?? (prefab.parts.some((p) => /leg/i.test(p.name)) ? 'leather' : undefined);
  const helmed = prefab.parts.some((p) => /helm|hood|hat|crown/i.test(p.name));
  const joined = bridgeGaps(id, repairHoles(id, prefab.parts));
  const dressed = joined.map((part) => paint(id, part, family, helmed));
  return { ...prefab, parts: withTeamParts(id, family, dressed) };
}
